import { memo, useCallback, KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react'
import { useHistory } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { CS_m } from 'back-end-api'

import Phone from 'assets/icons/communication/Phone'
import { useMiscDispatch } from 'services/Misc'
import {
  useAudioVisualsState,
  requestOpenAppointmentCall,
} from 'services/Misc/audioVisuals'
import { ROUTES } from 'Constants'

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(51, 184, 151, 0.6);
  }
  50% {
    transform: scale(1.08);
    box-shadow: 0 0 0 8px rgba(51, 184, 151, 0);
  }
`

const Wrapper = styled.button`
  border: none;
  background: #33b897;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  animation: ${pulse} 1.6s ease-in-out infinite;

  svg {
    width: 12px;
    height: 12px;
  }

  &:hover {
    background: #2ea889;
  }

  &:focus {
    outline: 2px solid rgba(51, 184, 151, 0.45);
    outline-offset: 2px;
  }
`

interface IncomingCallIconProps {
  appointment: CS_m.ModelsAppointment
  registrationId: number
}

/**
 * Small pulsing phone icon shown in the overview table when the current
 * doctor has a ready/in-progress incoming call for a registration.
 * Clicking it navigates to the patient summary page and asks the global
 * AVConsult component to re-open the incoming-call popup.
 */
const IncomingCallIcon = ({ appointment, registrationId }: IncomingCallIconProps) => {
  const history = useHistory()
  const miscDispatch = useMiscDispatch()
  const audioVisualState = useAudioVisualsState()

  const handleClick = useCallback(
    (e: ReactMouseEvent | ReactKeyboardEvent) => {
      // Prevent the row click from also firing
      e.stopPropagation()
      requestOpenAppointmentCall(miscDispatch, audioVisualState, appointment.id)
      history.push(`${ROUTES.PATIENT}/${registrationId}`)
    },
    [history, miscDispatch, audioVisualState, appointment.id, registrationId]
  )

  return (
    <Wrapper
      type="button"
      onClick={handleClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') handleClick(e)
      }}
      title="Pick up call"
      aria-label="Pick up call"
    >
      <Phone style={undefined} color="#ffffff" />
    </Wrapper>
  )
}

IncomingCallIcon.whyDidYouRender = true

export default memo(IncomingCallIcon)
